import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Sparkles, BookOpen, ClipboardList, Presentation, FileImage, Rocket,
  Upload, ArrowUpRight, Wand2, FileText, BookCopy, Mic, Workflow,
  CheckCircle2, AlertCircle, Mail, MessageSquare, Activity, Cpu,
} from "lucide-react";
import { PageHeader, GlassCard } from "../components/layout/PageHeader";
import { dashboardApi } from "../lib/api";
import { SAMPLE_PROJECTS, PIPELINE_STAGES } from "../lib/sample-data";
import { QUICK_CREATE } from "../lib/labs-config";

const AI_LAB_MODES = [
  { id: "write", label: "Write", icon: FileText, to: "/book-lab/editor/home" },
  { id: "design", label: "Design", icon: FileImage, to: "/flyer-lab" },
  { id: "layout", label: "Layout", icon: ClipboardList, to: "/workbook-lab" },
  { id: "publish", label: "Publish", icon: BookCopy, to: "/cover-lab" },
  { id: "automate", label: "Automate", icon: Workflow, to: "/automation-lab" },
  { id: "voice", label: "Voice", icon: Mic, to: "/voice-lab" },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    dashboardApi.stats().then(setStats).catch(() => setStats(null));
  }, []);

  const credits = stats?.credits || {
    gpt: 18450, claude: 12900, gemini: 8200, image: 540,
    voice_minutes: 320, sms: 1200, email_sends: 8400,
  };
  const automation = stats?.automation_activity || {
    emails_sent: 1284, sms_sent: 412, workflows_triggered: 67, failed: 2, upcoming: 9,
  };
  const brand = stats?.brand_health || { logo: true, colors: true, fonts: false, voice: true, templates: true };
  const pipeline = stats?.pipeline || { draft: 4, designed: 2, approved: 3, scheduled: 1, live: 5, completed: 12 };

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <PageHeader
        kicker="Workspace · Home"
        title="Welcome back, Founder."
        description="Your AI creative operating system. Plan, design, publish, and launch from one workspace."
        actions={
          <>
            <Link
              to="/create"
              data-testid="dashboard-create-cta"
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl pl-btn-aqua text-sm"
            >
              <Wand2 className="h-4 w-4" /> Create with AI
            </Link>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 px-4 h-11 rounded-xl border border-[var(--pl-border)] text-sm hover:border-[var(--pl-aqua)]/40 text-[var(--pl-text)]"
            >
              <ArrowUpRight className="h-4 w-4" /> View Projects
            </Link>
          </>
        }
      />

      {/* Quick Create */}
      <section data-testid="quick-create" className="space-y-3">
        <SectionTitle eyebrow="Quick Create" title="Start something new" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {QUICK_CREATE.map((q, i) => {
            const Icon = q.icon;
            return (
              <Link
                key={q.id}
                to={q.to}
                data-testid={`quick-create-${q.id}`}
                className="pl-fade-up group rounded-2xl p-4 border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 backdrop-blur-md pl-lift"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="h-10 w-10 rounded-xl grid place-items-center bg-[var(--pl-aqua)]/10 border border-[var(--pl-aqua)]/30 mb-3 group-hover:bg-[var(--pl-aqua)]/20 transition">
                  <Icon className="h-5 w-5 text-[var(--pl-aqua)]" strokeWidth={1.7} />
                </div>
                <div className="text-sm font-medium text-[var(--pl-text)]">{q.label}</div>
                <div className="text-[11px] text-[var(--pl-text-muted)] mt-0.5">AI · Templates · Brand</div>
              </Link>
            );
          })}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* AI Lab Widget */}
        <GlassCard className="p-6 lg:col-span-1" data-testid="ai-lab-widget">
          <SectionTitle eyebrow="AI Lab" title="Choose a mode" />
          <div className="grid grid-cols-2 gap-2 mt-3">
            {AI_LAB_MODES.map((m) => {
              const I = m.icon;
              return (
                <Link
                  key={m.id}
                  to={m.to}
                  data-testid={`ai-mode-${m.id}`}
                  className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-white/[0.03] border border-[var(--pl-border)] hover:border-[var(--pl-aqua)]/40 hover:bg-[var(--pl-aqua)]/5 transition"
                >
                  <I className="h-4 w-4 text-[var(--pl-aqua)]" strokeWidth={1.8} />
                  <span className="text-sm text-[var(--pl-text)]">{m.label}</span>
                </Link>
              );
            })}
          </div>
          <div className="mt-5 p-3 rounded-xl border border-[var(--pl-border)] bg-[var(--pl-aqua)]/5">
            <div className="flex items-center gap-2 text-[11px] text-[var(--pl-aqua)]">
              <Sparkles className="h-3.5 w-3.5" /> Premier Copilot is online
            </div>
            <div className="text-[12px] text-[var(--pl-text-muted)] mt-1">
              Ask: "Plan a 4-week launch for my new book."
            </div>
          </div>
        </GlassCard>

        {/* Launch Pipeline */}
        <GlassCard className="p-6 lg:col-span-2" data-testid="launch-pipeline">
          <SectionTitle eyebrow="Launch Pipeline" title="What's moving today" />
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4">
            {PIPELINE_STAGES.map((s) => (
              <div
                key={s.id}
                data-testid={`pipeline-${s.id}`}
                className="rounded-xl p-3 border border-[var(--pl-border)] bg-white/[0.03]"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
                  {s.label}
                </div>
                <div className="font-display text-2xl font-semibold mt-1 text-[var(--pl-text)]">
                  {pipeline[s.id] ?? 0}
                </div>
                <div className="mt-2 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full pl-signature-gradient"
                    style={{ width: `${Math.min(100, (pipeline[s.id] ?? 0) * 8)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Recent Projects */}
      <section data-testid="recent-projects" className="space-y-3">
        <div className="flex items-end justify-between">
          <SectionTitle eyebrow="Recent" title="Recent Projects" />
          <Link
            to="/projects"
            className="text-xs text-[var(--pl-aqua)] inline-flex items-center gap-1"
          >
            See all <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SAMPLE_PROJECTS.slice(0, 6).map((p, i) => (
            <ProjectCard key={p.id} project={p} delay={i * 50} />
          ))}
        </div>
      </section>

      {/* Brand health · AI Credits · Automation · Publishing */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <GlassCard className="p-5" data-testid="brand-health">
          <SectionTitle eyebrow="Brand Health" title="Identity readiness" />
          <ul className="mt-3 space-y-2 text-sm">
            {[
              ["Logo uploaded", brand.logo],
              ["Colors set", brand.colors],
              ["Fonts set", brand.fonts],
              ["Brand voice set", brand.voice],
              ["Templates created", brand.templates],
            ].map(([label, ok]) => (
              <li key={label} className="flex items-center gap-2 text-[var(--pl-text)]">
                {ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-yellow-400" />
                )}
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </GlassCard>

        <GlassCard className="p-5" data-testid="ai-credits">
          <SectionTitle eyebrow="AI Credits" title="Usage this cycle" />
          <ul className="mt-3 space-y-2 text-sm">
            {[
              ["GPT", credits.gpt, Cpu],
              ["Claude", credits.claude, Cpu],
              ["Gemini", credits.gemini, Cpu],
              ["Image", credits.image, FileImage],
              ["Voice (min)", credits.voice_minutes, Mic],
            ].map(([k, v, I]) => (
              <li key={k} className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-[var(--pl-text-muted)]">
                  <I className="h-4 w-4 text-[var(--pl-aqua)]" /> {k}
                </span>
                <span className="font-mono text-[var(--pl-text)]">{v.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </GlassCard>

        <GlassCard className="p-5" data-testid="automation-activity">
          <SectionTitle eyebrow="Automation" title="Recent activity" />
          <ul className="mt-3 space-y-2 text-sm">
            <Row icon={Mail} label="Emails sent" value={automation.emails_sent} />
            <Row icon={MessageSquare} label="SMS sent" value={automation.sms_sent} />
            <Row icon={Workflow} label="Workflows triggered" value={automation.workflows_triggered} />
            <Row icon={AlertCircle} label="Failed" value={automation.failed} tone="bad" />
            <Row icon={Activity} label="Upcoming" value={automation.upcoming} />
          </ul>
        </GlassCard>

        <GlassCard className="p-5" data-testid="publishing-readiness">
          <SectionTitle eyebrow="Publishing" title="Readiness" />
          <ul className="mt-3 space-y-2 text-sm">
            {[
              ["PDF ready", true],
              ["EPUB ready", false],
              ["Cover ready", true],
              ["KDP checklist", true],
              ["Audiobook status", false],
            ].map(([label, ok]) => (
              <li key={label} className="flex items-center justify-between">
                <span className="text-[var(--pl-text-muted)]">{label}</span>
                <span
                  className={
                    ok
                      ? "text-emerald-400 text-xs font-medium"
                      : "text-yellow-400 text-xs font-medium"
                  }
                >
                  {ok ? "Ready" : "Pending"}
                </span>
              </li>
            ))}
          </ul>
        </GlassCard>
      </div>
    </div>
  );
}

function SectionTitle({ eyebrow, title }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">
        {eyebrow}
      </div>
      <div className="font-display text-lg font-medium text-[var(--pl-text)]">{title}</div>
    </div>
  );
}

function Row({ icon: I, label, value, tone }) {
  return (
    <li className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-[var(--pl-text-muted)]">
        <I className="h-4 w-4 text-[var(--pl-aqua)]" /> {label}
      </span>
      <span className={`font-mono ${tone === "bad" ? "text-rose-300" : "text-[var(--pl-text)]"}`}>
        {value}
      </span>
    </li>
  );
}

function ProjectCard({ project, delay }) {
  return (
    <Link
      to="/projects"
      data-testid={`project-card-${project.id}`}
      className="pl-fade-up rounded-2xl overflow-hidden border border-[var(--pl-border)] bg-[var(--pl-surface)]/60 backdrop-blur-md pl-lift block"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="aspect-[16/10] bg-[var(--pl-bg-2)] relative overflow-hidden">
        {project.thumbnail ? (
          <img
            src={project.thumbnail}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full pl-signature-gradient" />
        )}
        <div className="absolute top-3 left-3 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.18em] backdrop-blur bg-black/40 text-white border border-white/10">
          {project.type}
        </div>
        <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md text-[10px] backdrop-blur bg-[var(--pl-aqua)]/20 border border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)] capitalize">
          {project.status}
        </div>
      </div>
      <div className="p-4">
        <div className="font-medium text-[var(--pl-text)] truncate">{project.title}</div>
        <div className="text-xs text-[var(--pl-text-muted)] truncate">{project.description}</div>
        <div className="mt-3 h-1 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full pl-signature-gradient"
            style={{ width: `${project.progress}%` }}
          />
        </div>
      </div>
    </Link>
  );
}
